// src/pages/NotFoundPage.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, ArrowLeft } from 'lucide-react';

const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div style={styles.container}>
      {/* Фоновые пятна — как в LandingPage */}
      <div style={styles.bgBlur1} />
      <div style={styles.bgBlur2} />
      <div style={styles.bgBlur3} />

      <div style={styles.card}>
        <div style={styles.errorCode}>404</div>

        <div style={styles.imageWrapper}>
          <img
            src="/images/грусть.png"
            alt="Грустная лама"
            style={styles.image}
            onError={(e) => {
              // Если кириллица не загрузится — пробуем резервную ламу
              e.currentTarget.src = '/images/load.png';
            }}
          />
        </div>

        <h1 style={styles.title}>Страница не найдена</h1>

        <p style={styles.subtitle}>
          Кажется, эта лама заблудилась в облаках и не нашла то, что вы искали.
        </p>

        <p style={styles.hint}>
          Возможно, страница была перемещена, удалена или вы ошиблись в адресе.
        </p>

        <div style={styles.buttons}>
          <button
            style={styles.primaryButton}
            onClick={() => navigate('/')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 28px rgba(106,17,203,0.5), 0 0 20px rgba(37,117,252,0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(106,17,203,0.35), 0 0 12px rgba(37,117,252,0.25)';
            }}
          >
            <Home size={18} style={{ marginRight: 8 }} />
            На главную
          </button>

          <button
            style={styles.secondaryButton}
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} style={{ marginRight: 8 }} />
            Назад
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== СТИЛИ ====================
const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(145deg, #f8fafd 0%, #ede9fe 100%)',
    padding: '20px',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
  },
  bgBlur1: {
    position: 'absolute',
    top: '-10%',
    left: '-5%',
    width: '600px',
    height: '600px',
    background: 'radial-gradient(circle, rgba(168,85,247,0.18) 0%, rgba(168,85,247,0) 70%)',
    borderRadius: '50%',
    filter: 'blur(80px)',
    zIndex: 0,
  },
  bgBlur2: {
    position: 'absolute',
    bottom: '-10%',
    right: '-5%',
    width: '700px',
    height: '700px',
    background: 'radial-gradient(circle, rgba(37,117,252,0.18) 0%, rgba(37,117,252,0) 70%)',
    borderRadius: '50%',
    filter: 'blur(80px)',
    zIndex: 0,
  },
  bgBlur3: {
    position: 'absolute',
    top: '40%',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '500px',
    height: '500px',
    background: 'radial-gradient(circle, rgba(236,72,153,0.1) 0%, rgba(236,72,153,0) 70%)',
    borderRadius: '50%',
    filter: 'blur(80px)',
    zIndex: 0,
  },
  card: {
    position: 'relative',
    zIndex: 1,
    background: 'rgba(255,255,255,0.65)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: 32,
    padding: '48px 40px',
    maxWidth: 520,
    width: '100%',
    textAlign: 'center',
    border: '1px solid rgba(255,255,255,0.7)',
    boxShadow: '0 25px 60px rgba(106,17,203,0.15), 0 0 40px rgba(37,117,252,0.08)',
  },
  errorCode: {
    fontSize: 72,
    fontWeight: 900,
    lineHeight: 1,
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: 8,
    letterSpacing: '-2px',
  },
  imageWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '20px 0 24px',
  },
  image: {
    width: 180,
    height: 180,
    objectFit: 'contain',
    filter: 'drop-shadow(0 8px 24px rgba(106,17,203,0.25))',
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: '#1a1a2e',
    margin: '0 0 12px',
  },
  subtitle: {
    fontSize: 15,
    color: '#4a4a68',
    lineHeight: 1.5,
    margin: '0 0 8px',
  },
  hint: {
    fontSize: 13,
    color: '#8a8a9e',
    lineHeight: 1.5,
    margin: '0 0 28px',
  },
  buttons: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  primaryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 28px',
    borderRadius: 40,
    border: 'none',
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 8px 20px rgba(106,17,203,0.35), 0 0 12px rgba(37,117,252,0.25)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  secondaryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 28px',
    borderRadius: 40,
    border: '1px solid rgba(37,117,252,0.4)',
    background: 'rgba(255,255,255,0.6)',
    color: '#2575fc',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

export default NotFoundPage;
